import { ExternalLink, Slack } from 'lucide-react';
import { type ReactNode, useId } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useFeatureFlagEnabled } from '@/lib/posthog/hooks/useFeatureFlagEnabled';
import { useWorkspacePlan } from '@/modules/billing/hooks/useWorkspacePlan';
import { useCompanyQuery } from '@/modules/companies/queries/useCompanyQuery';
import { HMLoadError } from '@/modules/hiring-manager/components/HMLoadError';
import {
  IntegrationRow,
  IntegrationStatus,
} from '@/modules/hiring-manager/components/IntegrationRow';
import { LinkedInIntegrationCard } from '@/modules/hiring-manager/linkedin/LinkedInIntegrationCard';
import { toSearchSuffix } from '@/modules/hiring-manager/utils/searchSuffix';
import { getHMTenantSelection } from '@/modules/hiring-manager/utils/tenant';
import { useConnectSlack } from '@/modules/integrations/hooks/useConnectSlack';
import { SlackNotificationChannelField } from '@/modules/slack-notification-channel/components/SlackNotificationChannelField';
import { Button } from '@/modules/ui/components/button/Button';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';
import { useUserExternalAccountsQuery } from '@/modules/user-external-accounts/queries/useUserExternalAccountsQuery';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';

import { BookingLinkRow } from './BookingLinkRow';
import { GoogleCalendarRow } from './GoogleCalendarRow';
import { McpIntegrationRow } from './McpIntegrationRow';

/**
 * Tools grouped by audience: where the HM works with Finlay (Slack), then
 * what Finlay uses to reach candidates as them. One row per tool with a
 * status dot, what uses it, and one action. The MCP row lists connected AI
 * apps; Google Calendar shows behind the `google-calendar` flag.
 */
export function HMIntegrations() {
  const [searchParams] = useSearchParams();
  const {
    plan,
    isLoading: isPlanLoading,
    error: planError,
  } = useWorkspacePlan();
  const isLinkedInAvailable =
    !isPlanLoading && !planError && plan !== 'managed';
  const connectSlack = useConnectSlack();
  const isGoogleCalendarEnabled =
    useFeatureFlagEnabled('googleCalendar') === true;
  const currentUserQuery = useCurrentUserQuery();
  const userId = currentUserQuery.data?.id;
  const tenantSelection = getHMTenantSelection(
    currentUserQuery.data,
    searchParams,
  );
  const tenantId = tenantSelection.selectedTenantId;
  const companyQuery = useCompanyQuery(tenantId ?? '', {
    enabled: Boolean(tenantId),
  });
  // Slack OAuth runs in a new tab: refetch on return so the row shows the
  // connection without a reload.
  const externalAccountsQuery = useUserExternalAccountsQuery(userId, {
    refetchOnWindowFocus: 'always',
  });

  const slackAccount = externalAccountsQuery.data?.find(
    (account) => account.type === 'Slack',
  );
  const isSlackConnected = slackAccount?.status === 'Active';
  const calendarAccount = externalAccountsQuery.data?.find(
    (account) => account.type === 'GoogleCalendar',
  );
  const isLoading =
    currentUserQuery.isLoading ||
    externalAccountsQuery.isLoading ||
    companyQuery.isLoading;
  const hasLoadError =
    !userId ||
    !tenantId ||
    (companyQuery.isError && companyQuery.data === undefined) ||
    (externalAccountsQuery.isError && externalAccountsQuery.data === undefined);
  const hasStaleStatus =
    externalAccountsQuery.isError && externalAccountsQuery.data !== undefined;

  const retry = () => {
    if (!userId) {
      void currentUserQuery.refetch();
      return;
    }

    void externalAccountsQuery.refetch();
    if (tenantId) void companyQuery.refetch();
  };

  const showLinkedIn =
    isLinkedInAvailable &&
    Boolean(companyQuery.data?.isAllowedToConnectLinkedIn);
  const showBookingLink = Boolean(companyQuery.data?.isSelfServiceWorkspace);

  return (
    <div className="flex max-w-[760px] flex-col gap-11">
      <div className="flex flex-col gap-1.5">
        <h1 className="text-[24px] leading-8 font-semibold tracking-[-0.02em]">
          Integrations
        </h1>
        <p className="text-muted-foreground text-[13px] leading-[19px]">
          Use Finlay from Slack, and connect what Finlay needs to reach
          candidates as you.
        </p>
      </div>

      {isLoading ? (
        <div className="divide-border flex flex-col divide-y border-y">
          <Skeleton className="my-5 h-10 w-full" />
        </div>
      ) : hasLoadError || !currentUserQuery.data || !tenantId ? (
        <HMLoadError
          description="We could not load your integrations. Check your connection and try again."
          isRetrying={
            currentUserQuery.isFetching ||
            externalAccountsQuery.isFetching ||
            companyQuery.isFetching
          }
          onRetry={retry}
          title="Integrations could not be loaded"
        />
      ) : (
        <>
          {hasStaleStatus && (
            <HMLoadError
              description="We could not refresh your Slack connection. Showing the last known status."
              isRetrying={externalAccountsQuery.isFetching}
              onRetry={retry}
              title="Slack status may be out of date"
            />
          )}

          <IntegrationSection
            caption="You don't need to open this app day to day. Finlay works with you in Slack, Claude and ChatGPT."
            title="Where you work with Finlay"
          >
            <IntegrationRow
              action={
                <Button
                  aria-label={
                    isSlackConnected ? 'Reconnect Slack' : 'Connect Slack'
                  }
                  className="h-8 rounded-md"
                  onClick={() => connectSlack('App', { newTab: true })}
                  size="sm"
                  variant={isSlackConnected ? 'outline' : 'primary'}
                >
                  {isSlackConnected ? 'Reconnect' : 'Connect Slack'}
                  <ExternalLink aria-hidden className="size-3.5" />
                </Button>
              }
              description="Chat with Finlay to create jobs, review shortlists and approve or reject candidates. Finlay posts updates and asks questions here too."
              icon={Slack}
              name="Slack"
              status={
                <IntegrationStatus isActive={isSlackConnected}>
                  {isSlackConnected
                    ? `Connected${slackAccount?.teamName ? ` · ${slackAccount.teamName}` : ''}`
                    : 'Not connected'}
                </IntegrationStatus>
              }
              usedBy="Jobs, candidates & decisions"
            >
              {isSlackConnected ? (
                <SlackNotificationChannelField layout="row" />
              ) : (
                <p className="text-muted-foreground text-[13px] leading-[19px]">
                  Need admin approval? Once your Slack admin approves Finlay,
                  click Connect Slack again.
                </p>
              )}
            </IntegrationRow>
            <McpIntegrationRow />
            {isGoogleCalendarEnabled && (
              <GoogleCalendarRow
                account={calendarAccount}
                userId={currentUserQuery.data.id}
              />
            )}
          </IntegrationSection>

          {(showLinkedIn || showBookingLink) && (
            <IntegrationSection
              caption="Finlay contacts candidates as you, so they hear from a person, not a tool."
              title="How candidates hear from you"
            >
              {showLinkedIn && (
                <LinkedInIntegrationCard
                  billingHref={`/hm/billing${toSearchSuffix(searchParams)}`}
                  tenantId={tenantId}
                />
              )}
              {showBookingLink && (
                <BookingLinkRow
                  tenantId={tenantId}
                  user={currentUserQuery.data}
                />
              )}
            </IntegrationSection>
          )}
        </>
      )}
    </div>
  );
}

interface IntegrationSectionProps {
  title: string;
  caption: string;
  children: ReactNode;
}

/** Integrations grouped by who they serve: you, or the candidates you reach. */
function IntegrationSection({
  title,
  caption,
  children,
}: IntegrationSectionProps) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="flex flex-col gap-3.5">
      <div className="flex flex-col gap-0.5">
        <h2 className="text-[15px] font-semibold" id={id}>
          {title}
        </h2>
        <p className="text-muted-foreground text-[13px] leading-[19px]">
          {caption}
        </p>
      </div>
      <ul className="divide-border flex flex-col divide-y border-y">
        {children}
      </ul>
    </section>
  );
}
